import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { formatNumber } from "src/i18n/formatNumber";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch, useSelector } from "src/store";

import { updateSearchQuery } from "@dashboard/devices/store";
import type { Group } from "@dashboard/devices/types";
import { setColumnVisibility } from "@dashboard/devices/utils/columnVisibility";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { SectionField, SectionFields } from "@shared/SectionPanel/SectionField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { TextLink } from "@shared/TextLink";

import { countWorkspaceDevices } from "./WorkspaceStatisticsSection.utils";

type WorkspaceStatisticsSectionProps = {
    workspace: API.Workspace;
};

type CountValueProps = {
    count: number;
    tooltip: string;
    path: string | null;
};

function CountValue(props: CountValueProps) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { i18n } = useTranslation();
    const { count, tooltip, path } = props;
    const displayCount = formatNumber(count, i18n.language);

    if (!path) {
        return displayCount;
    }

    const handleClick = () => {
        if (path === "/dashboard/devices") {
            UserSettingsService.setWorkspaceValue("devicePage.columnFilters", [{ id: "licenseStatus", value: ["Used"] }]);
            setColumnVisibility("licenseStatus");
            dispatch(updateSearchQuery(""));
        } else if (path === "/dashboard/groups") {
            UserSettingsService.setWorkspaceValue("groupPage.columnFilters", []);
            UserSettingsService.setWorkspaceValue("groupPage.search", "");
        }

        void navigate(path);
    };

    return (
        <TextLink onClick={handleClick} tooltip={tooltip}>
            {displayCount}
        </TextLink>
    );
}

export function WorkspaceStatisticsSection(props: WorkspaceStatisticsSectionProps) {
    const { t } = useTranslation(["common", "pages"]);
    const { effectiveWorkspaceSettings } = useAppSettingsService();
    const groups: Group[] = useSelector(selectGroups);

    const deviceCount = useMemo(() => countWorkspaceDevices(groups), [groups]);

    return (
        <SectionPanel data-testid="workspaceStatisticsSection" title={t("pages:workspacePage.section.title.workspaceStatistics")}>
            <SectionFields>
                <SectionField
                    label={t("common:label.devices")}
                    value={
                        <CountValue
                            count={deviceCount}
                            tooltip={t("common:tooltip.showDevices", { count: deviceCount })}
                            path={effectiveWorkspaceSettings.isDevicesPageEnabled ? "/dashboard/devices" : null}
                        />
                    }
                />
                <SectionField
                    label={t("common:label.groups")}
                    value={
                        <CountValue
                            count={groups.length}
                            tooltip={t("common:tooltip.showGroups")}
                            path={effectiveWorkspaceSettings.isGroupsPageEnabled ? "/dashboard/groups" : null}
                        />
                    }
                />
                <SectionField
                    label={t("common:label.members")}
                    value={
                        <CountValue
                            count={props.workspace.members?.length ?? 0}
                            tooltip={t("common:tooltip.showMembers")}
                            path="/workspace/members"
                        />
                    }
                />
            </SectionFields>
        </SectionPanel>
    );
}
